import { ProgressData as PlayerData, recordStudentSkillAttempt } from '../systems/ProgressData';
import Phaser from 'phaser';
import { GAME, HEX, CSS, FONTS, PHYS, LEVEL, getAnswerGemTheme } from '../config';
import { Player } from '../entities/Player';
import { mathEngine } from '../math/MathEngine';
import { SUMA_MISSIONS } from '../planets/additionMissions';
import { RESTA_MISSIONS } from '../planets/subtractionMissions';
import { MULTIPLICACION_MISSIONS } from '../planets/multiplicationMissions';
import { DIVISION_MISSIONS } from '../planets/divisionMissions';
import { GAME_MODE, getGameMode, getSessionExerciseCount } from '../systems/GameMode';
import { getBands } from '../util/responsive';
import { buildAdditionWorkedExample } from '../math/additionFeedback';
import { buildSubtractionWorkedExample } from '../math/subtractionFeedback';
import { buildMultiplicationWorkedExample } from '../math/multiplicationFeedback';
import { buildDivisionWorkedExample } from '../math/divisionFeedback';
import { PracticeImpulseEngine } from '../systems/PracticeImpulseEngine';
import { startScenePlay } from '../systems/PlayAuthorization';

const ANSWER_WIDTH = 200;
const ANSWER_PLATFORM_HEIGHT = 48;
const ANSWER_TOP_Y = 380;
const ANSWER_CONTENT_OFFSET = 60;

export class PracticeScene extends Phaser.Scene {
  constructor() {
    super('PracticeScene');
  }

  create() {
    // Protección: Práctica nunca se convierte en un atajo alterno a Competencia.
    if (getGameMode(this.registry) !== GAME_MODE.PRACTICE) {
      this.scene.start('LevelScene');
      return;
    }
    return startScenePlay(this, 'practice', ticket => this.createRun(ticket));
  }

  createRun(ticket) {
    this.progressAttemptId = PlayerData.beginAttempt?.(ticket);

    this.audio = this.registry.get('audio');
    this.physics.world.gravity.y = PHYS.GRAVITY;
    this.planetId = this.registry.get('currentPlanet') || 'suma';
    this.isResta = this.planetId === 'resta';
    this.isMult = this.planetId === 'mult';
    this.isDivision = this.planetId === 'division';
    const missions = this.isResta ? RESTA_MISSIONS : this.isMult ? MULTIPLICACION_MISSIONS : this.isDivision ? DIVISION_MISSIONS : SUMA_MISSIONS;
    this.mission = this.registry.get('currentMissionData') || missions[0];
    this.exerciseCount = getSessionExerciseCount(this.registry);
    this.operation = this.isResta ? 'resta' : this.isMult ? 'mult' : this.isDivision ? 'division' : 'suma';
    this.problems = mathEngine.generateMissionSet(this.mission.type, this.exerciseCount, this.operation);

    this.completed = 0;
    this.correctCount = 0;
    this.wrongCount = 0;
    this.exerciseDurations = [];
    // Contador propio de Práctica: no depende del reloj global de Phaser ni
    // de escenas anteriores.
    this.sessionElapsedMs = 0;
    this.exerciseElapsedMs = 0;
    this.sessionClockRunning = false;
    this.acceptingAnswer = false;
    this.answerArmedAt = 0;
    this.hasPlayerInput = false;
    this.previousPlayerBottom = null;
    this.ending = false;
    this.missionPaused = false;
    this.touch = { left: false, right: false, jump: false, jumpPressed: false };
    // Misma detección de controles que Competencia para conservar la experiencia.
    this.isTouch = this.hasTouchControls();
    // Retroalimentación pedagógica aislada por destreza. Resta se activa
    // únicamente en su Planeta 1; no modifica los demás planetas.
    this.additionPedagogyEnabled = this.planetId === 'suma';
    // Todos los planetas de Resta en Práctica comparten el mismo disparador:
    // el generador decide si muestra recta numérica o columnas.
    this.subtractionPedagogyEnabled = this.planetId === 'resta';
    // Multiplicación conserva un tutorial propio solo en Planeta 1.
    this.multiplicationPedagogyEnabled = this.planetId === 'mult' && this.mission?.num >= 1 && this.mission?.num <= 6;
    // División reutiliza el mismo motor en Planetas 1–8. Los Planetas 7–8
    // solo añaden la variante de divisor de dos cifras cuando el ejercicio lo requiere.
    this.divisionPedagogyEnabled = this.planetId === 'division' && this.mission?.num >= 1 && this.mission?.num <= 8;
    this.attemptsForCurrentExercise = 0;
    this.consecutiveErrors = 0;
    this.currentPracticeRecord = null;
    this.practiceLearningRecords = [];
    this.tutorialActive = false;
    this.activeTutorialId = null;
    this.additionTutorialCounter = 0;
    this.subtractionTutorialCounter = 0;
    this.multiplicationTutorialCounter = 0;
    this.divisionTutorialCounter = 0;
    // Un mismo motor de Práctica para los cuatro sistemas; Competencia nunca
    // crea esta escena ni puede activar un Impulso Cósmico.
    this.practiceImpulseWorld = this.isMult ? 'multiplicacion' : this.planetId;
    this.practiceImpulseEnabled = ['suma', 'resta', 'multiplicacion', 'division'].includes(this.practiceImpulseWorld);
    this.practiceImpulseEngine = this.practiceImpulseEnabled
      ? new PracticeImpulseEngine({ world: this.practiceImpulseWorld })
      : null;
    this.practiceImpulseActive = false;
    this.activePracticeImpulseId = null;
    this.practiceImpulseCounter = 0;
    this.handlePracticeImpulseComplete = this.handlePracticeImpulseComplete.bind(this);
    this.handleAdditionTutorialComplete = this.handleAdditionTutorialComplete.bind(this);
    this.handleSubtractionTutorialComplete = this.handleSubtractionTutorialComplete.bind(this);
    this.handleMultiplicationTutorialComplete = this.handleMultiplicationTutorialComplete.bind(this);
    this.handleDivisionTutorialComplete = this.handleDivisionTutorialComplete.bind(this);
    if (this.additionPedagogyEnabled && typeof window !== 'undefined') {
      window.addEventListener('tmag:practice-addition-tutorial-complete', this.handleAdditionTutorialComplete);
    }
    if (this.subtractionPedagogyEnabled && typeof window !== 'undefined') {
      window.addEventListener('tmag:practice-subtraction-tutorial-complete', this.handleSubtractionTutorialComplete);
    }
    if (this.multiplicationPedagogyEnabled && typeof window !== 'undefined') {
      window.addEventListener('tmag:practice-multiplication-tutorial-complete', this.handleMultiplicationTutorialComplete);
    }
    if (this.divisionPedagogyEnabled && typeof window !== 'undefined') {
      window.addEventListener('tmag:practice-division-tutorial-complete', this.handleDivisionTutorialComplete);
    }
    if (this.practiceImpulseEnabled && typeof window !== 'undefined') {
      window.addEventListener('tmag:practice-cosmic-impulse-complete', this.handlePracticeImpulseComplete);
    }

    this.buildWorld();
    this.buildInput();
    this.buildWorldFeedback();
    this.layout();
    this.scene.launch('PracticeHUDScene');
    this.startSessionTimerIfPlayable();
    this.showProblem();

    this.scale.on('resize', this.layout, this);
    this.scale.on('resize', this.recoverPlayerAfterResize, this);
    this.scale.on('resize', this.startSessionTimerIfPlayable, this);
    this.events.once('shutdown', () => {
      this.scale.off('resize', this.layout, this);
      this.scale.off('resize', this.recoverPlayerAfterResize, this);
      this.scale.off('resize', this.startSessionTimerIfPlayable, this);
      if (this.additionPedagogyEnabled && typeof window !== 'undefined') {
        window.removeEventListener('tmag:practice-addition-tutorial-complete', this.handleAdditionTutorialComplete);
      }
      if (this.subtractionPedagogyEnabled && typeof window !== 'undefined') {
        window.removeEventListener('tmag:practice-subtraction-tutorial-complete', this.handleSubtractionTutorialComplete);
      }
      if (this.multiplicationPedagogyEnabled && typeof window !== 'undefined') {
        window.removeEventListener('tmag:practice-multiplication-tutorial-complete', this.handleMultiplicationTutorialComplete);
      }
      if (this.divisionPedagogyEnabled && typeof window !== 'undefined') {
        window.removeEventListener('tmag:practice-division-tutorial-complete', this.handleDivisionTutorialComplete);
      }
      if (this.practiceImpulseEnabled && typeof window !== 'undefined') {
        window.removeEventListener('tmag:practice-cosmic-impulse-complete', this.handlePracticeImpulseComplete);
      }
      if (this.scene.isActive('PracticeHUDScene')) this.scene.stop('PracticeHUDScene');
    });
    this.cameras.main.fadeIn(220, 6, 3, 20);
  }

  buildWorld() {
    const skyKey = this.isResta ? 'bg_resta_day' : this.isMult ? 'bg_multiplicacion_forest' : this.isDivision ? 'bg_division_ocean' : 'bg_deepspace';
    this.background = this.add.image(0, 0, skyKey).setOrigin(0.5).setDepth(-8);

    this.ground = this.physics.add.staticImage(0, 0, this.platformKey()).setDepth(3);
    this.answerGroup = this.physics.add.staticGroup();
    this.answerPlates = [0, 1, 2].map(() => this.answerGroup.create(0, 0, this.platformKey()).setDepth(4));
    // Las identidades que lo necesitan aportan su propio contorno de contraste.
    // La gema conserva su tamaño y posición; el contorno solo mejora su lectura sobre fondos luminosos.
    this.answerGemOutlines = this.answerGemTheme().outlineTint
      ? [0, 1, 2].map(() => this.add.image(0, 0, 'crystal').setDepth(6))
      : [];
    this.answerCrystals = [0, 1, 2].map(() => this.add.image(0, 0, 'crystal').setDepth(7));
    this.answerFeedbacks = [0, 1, 2].map(() => this.add.graphics().setDepth(8));
    this.answerLabels = [0, 1, 2].map(() => this.add.text(0, 0, '', {
      fontFamily: FONTS.friendly,
      fontSize: '40px',
      fontStyle: '900',
      color: CSS.white,
      align: 'center',
    }).setOrigin(0.5).setDepth(9).setStroke('#061329', 8).setShadow(0, 3, '#000', 7, true, true));

    this.player = new Player(this, GAME.WIDTH / 2, 430);
    this.physics.add.collider(this.player, this.ground);
    // En Práctica las respuestas usan un aterrizaje visual propio. No se usa
    // el collider físico de las barras porque la caja del sprite puede quedar
    // desplazada respecto al dibujo y seleccionar respuestas automáticamente.
    this.physics.world.setBounds(0, 0, GAME.WIDTH, GAME.HEIGHT);
    this.player.setCollideWorldBounds(true);
  }

  platformKey() {
    return this.isResta ? 'platform_resta' : this.isDivision ? 'platform_division' : 'platform';
  }

  answerGemTheme() {
    return getAnswerGemTheme(this.operation);
  }

  buildInput() {
    this.cursors = this.input.keyboard.createCursorKeys();
    this.keys = this.input.keyboard.addKeys('A,D,W');
  }

  buildWorldFeedback() {
    // El aviso de acierto/error pertenece a la escena del mundo porque se
    // muestra junto a las plataformas. El resto del HUD vive en PracticeHUDScene.
    this.feedbackText = this.add.text(0, 0, '', {
      fontFamily: FONTS.display,
      fontSize: '30px',
      fontStyle: '900',
      color: CSS.green,
    }).setOrigin(0.5).setDepth(25).setStroke('#061329', 5).setShadow(0, 3, '#000', 6, true, true);
  }

  hasTouchControls() {
    return (this.sys.game.device.input?.touch)
      || (typeof window !== 'undefined'
        && (window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0));
  }

  groundY() {
    // Misma línea de piso del mundo de Competencia (1280 × 720).
    // Los controles viven por debajo de ella, no alteran el área jugable.
    return LEVEL.PLAT_TOP;
  }

  startSessionTimerIfPlayable() {
    // En vertical, el guardia visual impide jugar; no se cobra ese tiempo.
    if (this.tutorialActive || this.sessionClockRunning || this.scale.height > this.scale.width) return;
    this.sessionElapsedMs = 0;
    this.exerciseElapsedMs = 0;
    this.sessionClockRunning = true;
    this.refreshPracticeHud();
  }

  advanceSessionClock(delta) {
    // El contador solo avanza durante el gameplay visible en horizontal.
    if (!this.sessionClockRunning || this.scale.height > this.scale.width) return;
    const elapsed = Math.max(0, delta || 0);
    this.sessionElapsedMs += elapsed;
    this.exerciseElapsedMs += elapsed;
  }

  recoverPlayerAfterResize() {
    if (!this.player?.body || this.ending) return;

    const W = GAME.WIDTH;
    const groundY = this.groundY();
    const safeX = Phaser.Math.Clamp(this.player.x || W / 2, 42, W - 42);
    const safeY = groundY - 58;

    // Al rotar, Phaser reposiciona el piso. El cuerpo del astronauta debe
    // reiniciarse también, o conserva la altura del formato anterior y queda
    // debajo de la escena visible.
    this.player.setPosition(safeX, safeY);
    this.player.setVelocity(0, 0);
    this.player.setAcceleration(0, 0);
    this.player.body.enable = true;
    this.player.body.reset(safeX, safeY);
    this.player.coyote = PHYS.COYOTE_MS;
    this.player.buffer = 0;
    this.player.cutApplied = false;
    this.previousPlayerBottom = this.player.y + this.player.displayHeight * 0.46;
  }

  layout() {
    const viewportW = this.scale.width;
    const viewportH = this.scale.height;
    const bands = getBands(viewportW, viewportH);
    const camera = this.cameras.main;

    // Mundo de Práctica: misma referencia geométrica de Competencia.
    camera.setViewport(0, bands.playY, viewportW, bands.playH);
    camera.setZoom(Math.min(viewportW / GAME.WIDTH, bands.playH / GAME.HEIGHT));
    camera.centerOn(GAME.WIDTH / 2, GAME.HEIGHT / 2);

    this.isTouch = this.hasTouchControls();
    const worldW = GAME.WIDTH;
    const worldH = GAME.HEIGHT;
    // En pantallas táctiles panorámicas la cámara ve más ancho que el diseño
    // base. Usamos el rectángulo visible para que el fondo y el piso cubran
    // toda la escena, como ya hace Competencia, sin cambiar la zona jugable.
    const visibleW = viewportW / camera.zoom;
    const visibleH = bands.playH / camera.zoom;
    const groundY = this.groundY();
    // Práctica comparte la misma referencia vertical estable que Aventura.
    const answerY = ANSWER_TOP_Y;
    const xs = [worldW * 0.22, worldW * 0.5, worldW * 0.78];
    const answerW = Math.min(ANSWER_WIDTH, Math.max(108, worldW * 0.25));
    const answerH = ANSWER_PLATFORM_HEIGHT;

    this.background.setPosition(worldW / 2, worldH / 2).setDisplaySize(visibleW, visibleH);
    this.ground.setPosition(worldW / 2, groundY + 30).setDisplaySize(visibleW + 80, 64).refreshBody();
    const answerGemTheme = this.answerGemTheme();
    this.answerPlates.forEach((plate, index) => {
      plate.setPosition(xs[index], answerY + answerH / 2).setDisplaySize(answerW, answerH).refreshBody();
      // Plataformas de una vía: el astronauta puede cruzarlas lateralmente y desde abajo.
      plate.body.checkCollision.down = false;
      plate.body.checkCollision.left = false;
      plate.body.checkCollision.right = false;
      plate.body.checkCollision.up = true;
      // Gema y número siempre comparten centro visual en todos los viewports.
      const outline = this.answerGemOutlines[index];
      if (outline && answerGemTheme.outlineTint) {
        outline
          .setPosition(xs[index], answerY - ANSWER_CONTENT_OFFSET)
          .setScale(answerGemTheme.scale * answerGemTheme.outlineScale)
          .setTint(answerGemTheme.outlineTint)
          .setAlpha(answerGemTheme.outlineAlpha);
      }
      this.answerCrystals[index].setPosition(xs[index], answerY - ANSWER_CONTENT_OFFSET).setScale(answerGemTheme.scale).setTint(answerGemTheme.tint);
      this.answerLabels[index].setPosition(xs[index], answerY - ANSWER_CONTENT_OFFSET).setFontSize(44);
    });

    const spawnX = Phaser.Math.Clamp(this.player?.x || worldW / 2, 42, worldW - 42);
    if (this.player?.body?.blocked?.down) this.player.setPosition(spawnX, groundY - 58);
    this.feedbackText.setPosition(worldW / 2, answerY - 130).setFontSize(30);
    this.events.emit('practice-layout');
    this.refreshPracticeHud();
  }

  clearAnswerFeedbacks() {
    this.answerFeedbacks.forEach(feedback => feedback.clear());
  }

  drawAnswerFeedback(index, tint) {
    const plate = this.answerPlates[index];
    const feedback = this.answerFeedbacks[index];
    if (!plate || !feedback) return;
    const width = plate.displayWidth;
    const height = plate.displayHeight;
    const left = plate.x - width / 2;
    const top = plate.y - height / 2;
    feedback.clear();
    // Capa completa: colorea toda la plataforma sin ocultar el número ni sus perlas.
    feedback.fillStyle(tint, 0.95);
    feedback.fillRoundedRect(left, top, width, height, Math.min(14, height / 3));
    feedback.lineStyle(3, 0xffffff, 0.94);
    feedback.strokeRoundedRect(left, top, width, height, Math.min(14, height / 3));
    feedback.fillStyle(0xf5fff9, 0.98);
    const pearlY = top + height * 0.67;
    const pearlW = Math.max(6, width * 0.045);
    const pearlH = Math.max(5, height * 0.12);
    [-0.27, -0.09, 0.09, 0.27].forEach(offset => {
      feedback.fillRoundedRect(plate.x + width * offset - pearlW / 2, pearlY - pearlH / 2, pearlW, pearlH, pearlH / 2);
    });
  }

  refreshPracticeHud() {
    // PracticeHUDScene escucha este evento y se dibuja en coordenadas reales
    // de pantalla, sin depender del zoom o scroll de la cámara del mundo.
    this.events.emit('practice-state');
  }

  isAdditionPedagogyActive() {
    return this.additionPedagogyEnabled && this.currentProblem && !this.ending;
  }

  isSubtractionPedagogyActive() {
    return this.subtractionPedagogyEnabled && this.currentProblem && !this.ending;
  }

  isMultiplicationPedagogyActive() {
    return this.multiplicationPedagogyEnabled && this.currentProblem && !this.ending;
  }

  isDivisionPedagogyActive() {
    return this.divisionPedagogyEnabled && this.currentProblem && !this.ending;
  }

  isPedagogyActive() {
    return this.isAdditionPedagogyActive() || this.isSubtractionPedagogyActive() || this.isMultiplicationPedagogyActive() || this.isDivisionPedagogyActive();
  }

  clearPracticeControls() {
    this.setControl('left', false);
    this.setControl('right', false);
    this.setControl('jump', false);
  }

  resetWorldFeedback() {
    this.feedbackText
      .setText('')
      .setFontSize(30)
      .setWordWrapWidth(0, true)
      .setColor(CSS.green)
      .setAlpha(1);
  }

  finalizeCurrentPracticeRecord({ needsReview = false } = {}) {
    if (!this.currentPracticeRecord) return;
    this.currentPracticeRecord.needsReview = needsReview;
    this.currentPracticeRecord.completed = true;
    this.currentPracticeRecord.elapsedMs = this.exerciseElapsedMs;
    this.practiceLearningRecords.push({ ...this.currentPracticeRecord });
    this.currentPracticeRecord = null;
  }

  continueAfterAdditionError() {
    this.exerciseDurations.push(this.exerciseElapsedMs);
    this.completed += 1;
    this.finalizeCurrentPracticeRecord();
    this.refreshPracticeHud();
    this.time.delayedCall(850, () => this.showProblem());
  }

  handleAdditionIncorrectAnswer() {
    this.practiceImpulseEngine?.recordIncorrect();
    this.attemptsForCurrentExercise += 1;
    const record = this.currentPracticeRecord;
    if (record) record.attemptsForCurrentExercise = this.attemptsForCurrentExercise;
    this.wrongCount += 1;
    this.consecutiveErrors += 1;
    this.refreshPracticeHud();
    this.audio?.playWrong?.(1);
    this.cameras.main.flash(90, 255, 65, 110, false);

    // Primer y segundo error consecutivo: el ejercicio termina normalmente,
    // sin pista, ayuda ni tutorial. El siguiente ejercicio sigue la secuencia.
    if (this.consecutiveErrors < 3) {
      this.continueAfterAdditionError();
      return;
    }

    // Tercer error consecutivo: se abre el tutorial existente para explicar
    // exactamente el ejercicio que produjo este tercer error.
    if (record) record.requiredWorkedExample = true;
    this.tutorialActive = true;
    this.sessionClockRunning = false;
    this.acceptingAnswer = false;
    this.clearPracticeControls();
    this.player.setVelocity(0, 0);
    this.feedbackText.setColor(CSS.gold).setFontSize(26).setWordWrapWidth(0, true).setText('VEAMOS CÓMO SE HACE');
    this.additionTutorialCounter += 1;
    this.activeTutorialId = `addition-${this.time.now}-${this.additionTutorialCounter}`;
    const detail = {
      sessionId: this.activeTutorialId,
      tutorial: buildAdditionWorkedExample(this.currentProblem),
    };
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tmag:practice-addition-tutorial-open', { detail }));
    }
  }

  handleAdditionTutorialComplete(event) {
    const sessionId = event?.detail?.sessionId;
    if (!this.tutorialActive || sessionId !== this.activeTutorialId || this.ending) return;

    this.tutorialActive = false;
    this.activeTutorialId = null;
    this.consecutiveErrors = 0;
    // El impulso no se muestra al cerrar el tutorial: espera evidencia real,
    // una respuesta correcta posterior, antes de poder activarse.
    this.practiceImpulseEngine?.markTutorialCompleted();
    this.completed += 1;
    this.exerciseDurations.push(this.exerciseElapsedMs);
    this.finalizeCurrentPracticeRecord({ needsReview: true });
    this.sessionClockRunning = this.scale.width > this.scale.height;
    this.resetWorldFeedback();
    this.feedbackText.setColor(CSS.green).setText('¡SIGAMOS PRACTICANDO!');
    this.refreshPracticeHud();
    this.time.delayedCall(460, () => this.showProblem());
  }

  getPracticeImpulseAfterCorrect() {
    if (!this.practiceImpulseEnabled || !this.practiceImpulseEngine || this.completed >= this.exerciseCount) return null;
    return this.practiceImpulseEngine.evaluateCorrect(this.sessionElapsedMs);
  }

  openPracticeImpulse(impulse) {
    if (!impulse || this.ending || !this.practiceImpulseEnabled) {
      this.showProblem();
      return;
    }
    this.practiceImpulseActive = true;
    this.sessionClockRunning = false;
    this.acceptingAnswer = false;
    this.clearPracticeControls();
    this.player.setVelocity(0, 0);
    this.feedbackText.setText('');
    this.practiceImpulseCounter += 1;
    this.activePracticeImpulseId = `practice-impulse-${this.time.now}-${this.practiceImpulseCounter}`;
    this.practiceImpulseEngine.registerShown(this.sessionElapsedMs);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tmag:practice-cosmic-impulse-open', {
        detail: {
          sessionId: this.activePracticeImpulseId,
          planetId: this.planetId,
          trigger: impulse.trigger,
          message: impulse.message,
        },
      }));
    }
  }

  handlePracticeImpulseComplete(event) {
    const sessionId = event?.detail?.sessionId;
    if (!this.practiceImpulseActive || sessionId !== this.activePracticeImpulseId || this.ending) return;
    this.practiceImpulseActive = false;
    this.activePracticeImpulseId = null;
    this.sessionClockRunning = this.scale.width > this.scale.height;
    this.resetWorldFeedback();
    this.showProblem();
  }

  handleSubtractionIncorrectAnswer() {
    this.practiceImpulseEngine?.recordIncorrect();
    this.attemptsForCurrentExercise += 1;
    const record = this.currentPracticeRecord;
    if (record) record.attemptsForCurrentExercise = this.attemptsForCurrentExercise;
    this.wrongCount += 1;
    this.consecutiveErrors += 1;
    this.refreshPracticeHud();
    this.audio?.playWrong?.(1);
    this.cameras.main.flash(90, 255, 65, 110, false);

    if (this.consecutiveErrors < 3) {
      this.continueAfterAdditionError();
      return;
    }

    if (record) record.requiredWorkedExample = true;
    this.tutorialActive = true;
    this.sessionClockRunning = false;
    this.acceptingAnswer = false;
    this.clearPracticeControls();
    this.player.setVelocity(0, 0);
    this.feedbackText.setColor(CSS.gold).setFontSize(26).setWordWrapWidth(0, true).setText('VEAMOS CÓMO SE HACE');
    this.subtractionTutorialCounter += 1;
    this.activeTutorialId = `subtraction-${this.time.now}-${this.subtractionTutorialCounter}`;
    const detail = {
      sessionId: this.activeTutorialId,
      tutorial: buildSubtractionWorkedExample(this.currentProblem),
    };
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tmag:practice-subtraction-tutorial-open', { detail }));
    }
  }

  handleSubtractionTutorialComplete(event) {
    const sessionId = event?.detail?.sessionId;
    if (!this.tutorialActive || sessionId !== this.activeTutorialId || this.ending) return;

    this.tutorialActive = false;
    this.activeTutorialId = null;
    this.consecutiveErrors = 0;
    this.practiceImpulseEngine?.markTutorialCompleted();
    this.completed += 1;
    this.exerciseDurations.push(this.exerciseElapsedMs);
    this.finalizeCurrentPracticeRecord({ needsReview: true });
    this.sessionClockRunning = this.scale.width > this.scale.height;
    this.resetWorldFeedback();
    this.feedbackText.setColor(CSS.green).setText('¡SIGAMOS PRACTICANDO!');
    this.refreshPracticeHud();
    this.time.delayedCall(460, () => this.showProblem());
  }

  handleMultiplicationIncorrectAnswer() {
    this.practiceImpulseEngine?.recordIncorrect();
    this.attemptsForCurrentExercise += 1;
    const record = this.currentPracticeRecord;
    if (record) record.attemptsForCurrentExercise = this.attemptsForCurrentExercise;
    this.wrongCount += 1;
    this.consecutiveErrors += 1;
    this.refreshPracticeHud();
    this.audio?.playWrong?.(1);
    this.cameras.main.flash(90, 255, 65, 110, false);

    // El primer y segundo error continúan sin pista. En el tercero se explica
    // el ejercicio real como grupos iguales.
    if (this.consecutiveErrors < 3) {
      this.continueAfterAdditionError();
      return;
    }

    if (record) record.requiredWorkedExample = true;
    this.tutorialActive = true;
    this.sessionClockRunning = false;
    this.acceptingAnswer = false;
    this.clearPracticeControls();
    this.player.setVelocity(0, 0);
    this.feedbackText.setColor(CSS.gold).setFontSize(26).setWordWrapWidth(0, true).setText('VEAMOS CÓMO SE HACE');
    this.multiplicationTutorialCounter += 1;
    this.activeTutorialId = `multiplication-${this.time.now}-${this.multiplicationTutorialCounter}`;
    const detail = {
      sessionId: this.activeTutorialId,
      tutorial: buildMultiplicationWorkedExample(this.currentProblem, this.mission?.num),
    };
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tmag:practice-multiplication-tutorial-open', { detail }));
    }
  }

  handleMultiplicationTutorialComplete(event) {
    const sessionId = event?.detail?.sessionId;
    if (!this.tutorialActive || sessionId !== this.activeTutorialId || this.ending) return;

    this.tutorialActive = false;
    this.activeTutorialId = null;
    this.consecutiveErrors = 0;
    this.practiceImpulseEngine?.markTutorialCompleted();
    this.completed += 1;
    this.exerciseDurations.push(this.exerciseElapsedMs);
    this.finalizeCurrentPracticeRecord({ needsReview: true });
    this.sessionClockRunning = this.scale.width > this.scale.height;
    this.resetWorldFeedback();
    this.feedbackText.setColor(CSS.green).setText('¡SIGAMOS PRACTICANDO!');
    this.refreshPracticeHud();
    this.time.delayedCall(460, () => this.showProblem());
  }

  handleDivisionIncorrectAnswer() {
    this.practiceImpulseEngine?.recordIncorrect();
    this.attemptsForCurrentExercise += 1;
    const record = this.currentPracticeRecord;
    if (record) record.attemptsForCurrentExercise = this.attemptsForCurrentExercise;
    this.wrongCount += 1;
    this.consecutiveErrors += 1;
    this.refreshPracticeHud();
    this.audio?.playWrong?.(1);
    this.cameras.main.flash(90, 255, 65, 110, false);

    if (this.consecutiveErrors < 3) {
      this.continueAfterAdditionError();
      return;
    }

    if (record) record.requiredWorkedExample = true;
    this.tutorialActive = true;
    this.sessionClockRunning = false;
    this.acceptingAnswer = false;
    this.clearPracticeControls();
    this.player.setVelocity(0, 0);
    this.feedbackText.setColor(CSS.gold).setFontSize(26).setWordWrapWidth(0, true).setText('VEAMOS CÓMO SE HACE');
    this.divisionTutorialCounter += 1;
    this.activeTutorialId = `division-${this.time.now}-${this.divisionTutorialCounter}`;
    const detail = {
      sessionId: this.activeTutorialId,
      tutorial: buildDivisionWorkedExample(this.currentProblem, {
        // Planeta 8 reutiliza el mismo motor según el divisor real del ejercicio.
        includeVisualRepresentation: this.mission?.num === 1,
        showBringArrow: this.mission?.num >= 2 && this.mission?.num <= 8,
        formatLargeNumbers: this.mission?.num >= 5 && this.mission?.num <= 8,
        explainTwoDigitDivisor: this.mission?.num === 7 || (this.mission?.num === 8 && this.currentProblem?.b >= 10),
      }),
    };
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tmag:practice-division-tutorial-open', { detail }));
    }
  }

  handleDivisionTutorialComplete(event) {
    const sessionId = event?.detail?.sessionId;
    if (!this.tutorialActive || sessionId !== this.activeTutorialId || this.ending) return;

    this.tutorialActive = false;
    this.activeTutorialId = null;
    this.consecutiveErrors = 0;
    this.practiceImpulseEngine?.markTutorialCompleted();
    this.completed += 1;
    this.exerciseDurations.push(this.exerciseElapsedMs);
    this.finalizeCurrentPracticeRecord({ needsReview: true });
    this.sessionClockRunning = this.scale.width > this.scale.height;
    this.resetWorldFeedback();
    this.feedbackText.setColor(CSS.green).setText('¡SIGAMOS PRACTICANDO!');
    this.refreshPracticeHud();
    this.time.delayedCall(460, () => this.showProblem());
  }

  setControl(key, pressed) {
    if (!this.touch || this.missionPaused) return;
    if (key === 'jump') {
      if (pressed) this.touch.jumpPressed = true;
      this.touch.jump = pressed;
      return;
    }
    this.touch[key] = pressed;
  }

  showProblem() {
    const problem = this.problems[this.completed];
    if (!problem) {
      this.finishPractice();
      return;
    }
    this.currentProblem = problem;
    this.attemptsForCurrentExercise = 0;
    this.currentPracticeRecord = this.isPedagogyActive() ? {
      originalQuestion: problem.text,
      correctAnswer: problem.answer,
      attemptsForCurrentExercise: 0,
      requiredWorkedExample: false,
      needsReview: false,
    } : null;
    this.acceptingAnswer = true;
    // Cada ejercicio exige una nueva acción consciente del jugador.
    this.answerArmedAt = this.time.now + 220;
    this.hasPlayerInput = false;
    this.previousPlayerBottom = null;
    this.exerciseElapsedMs = 0;
    this.feedbackText.setText('').setAlpha(1);
    this.clearAnswerFeedbacks();
    this.refreshPracticeHud();
    problem.choices.forEach((choice, index) => {
      this.answerPlates[index].setData('choice', choice).setAlpha(1).clearTint();
      this.answerLabels[index].setText(choice.label || new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(choice.value)).setAlpha(1);
    });

    const W = GAME.WIDTH;
    const H = GAME.HEIGHT;
    const floorY = this.groundY();
    this.touch.left = false;
    this.touch.right = false;
    this.touch.jump = false;
    this.touch.jumpPressed = false;
    this.player.setPosition(W / 2, floorY - 58).setVelocity(0, 0).setVisible(true);
    this.player.body.enable = true;
    this.player.body.reset(this.player.x, this.player.y);
    this.previousPlayerBottom = this.player.y + this.player.displayHeight * 0.46;
  }

  checkManualAnswerLanding() {
    if (!this.acceptingAnswer || this.ending || !this.hasPlayerInput || this.time.now < this.answerArmedAt) return;
    const playerBottom = this.player.y + this.player.displayHeight * 0.46;
    const previousBottom = this.previousPlayerBottom ?? playerBottom;
    const isFalling = this.player.body.velocity.y > 0;

    if (!isFalling) return;
    for (const plate of this.answerPlates) {
      const plateTop = plate.y - plate.displayHeight / 2;
      const plateLeft = plate.x - plate.displayWidth / 2;
      const plateRight = plate.x + plate.displayWidth / 2;
      const playerLeft = this.player.x - this.player.displayWidth * 0.24;
      const playerRight = this.player.x + this.player.displayWidth * 0.24;
      const overlapsHorizontally = playerRight > plateLeft + 10 && playerLeft < plateRight - 10;
      const crossedPlatformTop = previousBottom < plateTop && playerBottom >= plateTop;

      if (!overlapsHorizontally || !crossedPlatformTop) continue;
      // Alinea el dibujo sobre la plataforma antes de mostrar el feedback.
      this.player.setPosition(this.player.x, plateTop - this.player.displayHeight * 0.46).setVelocity(0, 0);
      this.resolveAnswer(plate.getData('choice'), plate);
      return;
    }
  }

  resolveAnswer(choice, plate) {
    if (!this.acceptingAnswer) return;
    this.acceptingAnswer = false;
    const correct = !!choice.correct;
    recordStudentSkillAttempt(this.planetId, this.mission.index, this.currentProblem?.skillTag, correct);
    const tint = correct ? HEX.green : HEX.red;
    this.drawAnswerFeedback(this.answerPlates.indexOf(plate), tint);

    // Suma-Práctica acumula errores consecutivos entre ejercicios; una
    // respuesta correcta reinicia la secuencia y el tercer error abre el
    // tutorial existente del ejercicio que falló.
    if (!correct && this.isAdditionPedagogyActive()) {
      this.handleAdditionIncorrectAnswer();
      return;
    }
    if (!correct && this.isSubtractionPedagogyActive()) {
      this.handleSubtractionIncorrectAnswer();
      return;
    }
    if (!correct && this.isMultiplicationPedagogyActive()) {
      this.handleMultiplicationIncorrectAnswer();
      return;
    }
    if (!correct && this.isDivisionPedagogyActive()) {
      this.handleDivisionIncorrectAnswer();
      return;
    }

    this.exerciseDurations.push(this.exerciseElapsedMs);
    if (correct) {
      this.correctCount += 1;
      this.consecutiveErrors = 0;
    }
    else this.wrongCount += 1;
    this.completed += 1;
    if (correct && this.isPedagogyActive()) this.finalizeCurrentPracticeRecord();
    const pendingImpulse = correct ? this.getPracticeImpulseAfterCorrect() : null;
    this.refreshPracticeHud();

    this.feedbackText.setColor(correct ? CSS.green : CSS.red).setText(correct ? '¡CORRECTO!' : '¡SEGUIMOS PRACTICANDO!');
    this.audio?.[correct ? 'playCorrect' : 'playWrong']?.(1);
    this.cameras.main.flash(correct ? 120 : 90, correct ? 40 : 255, correct ? 255 : 65, correct ? 150 : 110, false);

    if (correct) {
      this.tweens.add({ targets: this.player, y: this.player.y - 22, duration: 150, yoyo: true, ease: 'Sine.out' });
      this.add.particles(plate.x, plate.y - 50, 'spark', {
        speed: { min: 80, max: 240 },
        lifespan: 520,
        scale: { start: 0.8, end: 0 },
        quantity: 18,
        tint: [HEX.green, HEX.white],
        blendMode: 'ADD',
      }).explode(18, plate.x, plate.y - 50);
    } else {
      this.tweens.add({ targets: this.player, x: this.player.x - 10, duration: 80, yoyo: true, repeat: 2 });
    }

    this.time.delayedCall(correct ? 750 : 850, () => {
      if (pendingImpulse) this.openPracticeImpulse(pendingImpulse);
      else this.showProblem();
    });
  }

  finishPractice() {
    if (this.ending) return;
    this.ending = true;
    const durationMs = this.sessionElapsedMs;
    this.scene.start('PracticeResultScene', {
      total: this.exerciseCount,
      completed: this.completed,
      correct: this.correctCount,
      wrong: this.wrongCount,
      durationMs,
      exerciseDurations: [...this.exerciseDurations],
      learningRecords: [...this.practiceLearningRecords],
      missionName: this.mission.name,
      missionSkill: this.mission.skill,
      ...(this.progressAttemptId ? { progressAttemptId: this.progressAttemptId } : {}),
    });
  }

  pauseMission() {
    if (this.ending || this.missionPaused) return false;
    this.missionPaused = true;
    Object.assign(this.touch || {}, { left: false, right: false, jump: false, jumpPressed: false });
    this.physics.world.pause();
    this.tweens.pauseAll?.();
    this.scene.pause();
    return true;
  }

  resumeMission() {
    if (!this.missionPaused) return false;
    this.missionPaused = false;
    this.physics.world.resume();
    this.tweens.resumeAll?.();
    this.scene.resume();
    return true;
  }

  restartMission() {
    if (this.ending) return;
    this.missionPaused = false;
    this.physics.world.resume();
    this.tweens.resumeAll?.();
    this.scene.resume();
    this.scene.stop('PracticeHUDScene');
    this.scene.restart();
  }

  exitMission() {
    if (this.ending) return;
    this.missionPaused = false;
    this.physics.world.resume();
    this.tweens.resumeAll?.();
    this.scene.resume();
    this.scene.stop('PracticeHUDScene');
    this.scene.start('MissionScene');
  }

  update(_, delta) {
    if (this.awaitingPlayAuthorization) return;
    if (!this.player || this.ending || this.missionPaused || this.tutorialActive || this.practiceImpulseActive) return;
    this.advanceSessionClock(delta);
    const input = {
      left: this.cursors.left.isDown || this.keys.A.isDown || this.touch.left,
      right: this.cursors.right.isDown || this.keys.D.isDown || this.touch.right,
      jumpDown: Phaser.Input.Keyboard.JustDown(this.cursors.space)
        || Phaser.Input.Keyboard.JustDown(this.cursors.up)
        || Phaser.Input.Keyboard.JustDown(this.keys.W)
        || this.touch.jumpPressed,
      jumpHeld: this.cursors.space.isDown || this.cursors.up.isDown || this.keys.W.isDown || this.touch.jump,
    };
    this.touch.jumpPressed = false;
    if (input.left || input.right || input.jumpDown) this.hasPlayerInput = true;
    this.player.handleInput(input, delta);
    this.player.x = Phaser.Math.Clamp(this.player.x, 35, GAME.WIDTH - 35);
    this.checkManualAnswerLanding();
    this.previousPlayerBottom = this.player.y + this.player.displayHeight * 0.46;
    this.refreshPracticeHud();
  }
}
